import { useEffect, useId, useRef, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type BottomSheetProps = {
  title: string
  /** Riga sotto il titolo (es. "Bicchiere 3 · G1/G5"). */
  subtitle?: ReactNode
  onClose: () => void
  children: ReactNode
  testId?: string
  /** Ancorato in basso anche da `sm:` (la schedina: "si deve aprire sotto"); altrimenti da `sm:` è centrato. */
  docked?: boolean
}

/**
 * Pannello della variante notte: ancorato in basso sotto `sm` (maniglia, safe area) e centrato da `sm:` (salvo `docked`).
 * Div e non `<dialog>`: la posizione in basso va controllata a mano, insieme a focus, Esc e scroll della pagina.
 * Tocco sullo sfondo o Esc chiudono; alla chiusura il focus torna dove era.
 */
export function BottomSheet({ title, subtitle, onClose, children, testId, docked = false }: BottomSheetProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  // `onClose` cambia a ogni render del genitore (il cronometro lo ridisegna ogni secondo): in un ref,
  // così l'ascoltatore di Esc non si ricrea di continuo.
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    panelRef.current?.focus()
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && closeRef.current()
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
      previouslyFocused?.focus()
    }
  }, [])

  return (
    <div className={cn('fixed inset-0 z-50 flex items-end justify-center bg-ink/60 backdrop-blur-sm', !docked && 'sm:items-center sm:p-4')} onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'night max-h-[85dvh] w-full max-w-md overflow-y-auto rounded-t-3xl border-t border-paper/15 px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-[0_-12px_40px_-12px_black] focus:outline-none motion-safe:animate-sheet-up',
          docked ? 'sm:border-x' : 'sm:rounded-3xl sm:border sm:pt-6',
        )}
      >
        <span aria-hidden="true" className={cn('mx-auto mb-4 block h-1 w-10 rounded-full bg-paper/25', !docked && 'sm:hidden')} />
        <h2 id={titleId} className="text-center font-display text-2xl font-medium tracking-tight text-paper">
          {title}
        </h2>
        {subtitle && <p className="mt-1 text-center font-body text-sm text-paper/60">{subtitle}</p>}
        <div className="mt-5">{children}</div>
      </div>
    </div>
  )
}

/** Bottone pieno largo dello sheet: 52px, facile da prendere col pollice. */
export const sheetButtonClass =
  'flex min-h-13 w-full items-center justify-center gap-2 rounded-2xl px-5 font-body text-base font-semibold transition-[transform,background-color] duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50'
